import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { type OperatorInvitationView } from '@vello/api-client';
import { invitationTokenOf } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { AuthLayout } from '../components/layout';
import { Button, Field, Notice, TextInput, AuthCard, Stack, Text, CodeColumns, TapLink } from '@vello/web-theme';

/**
 * The invitation landing (W8-OPERATORS-AND-SCOPES, D449, ADR-0078; findings A-05, A-06) — `/invitation?token=…`, public.
 *
 * An invited operator (or one whose second factor an administrator reset) opens the link: the server mints the operator's
 * OWN authenticator secret, sealed at rest, and shows its set-up key once. The operator adds it to an authenticator app,
 * types the first code and — for a new account — chooses its password. The single-use recovery codes are shown ONCE on
 * success. No shared secret is involved at any step.
 */
export function InvitationScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();

  const [token] = useState<string>(() => invitationTokenOf(typeof window !== 'undefined' ? window.location.search : ''));
  const [view, setView] = useState<OperatorInvitationView | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<readonly string[] | undefined>(undefined);
  const [msg, setMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (token === '') {
      setProblem(t('inv.invalid'));
      return;
    }
    void runtime.operatorAuth.inspectInvitation(token).then((res) => {
      if (res.ok) setView(res.value);
      else setProblem(res.error.status === 503 ? t('inv.unavailable') : t('inv.invalid'));
    });
  }, [runtime, token, t]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    if (view === undefined) return;
    if (view.passwordRequired && password !== confirm) {
      setMsg(t('inv.mismatch'));
      return;
    }
    setBusy(true);
    const res = await runtime.operatorAuth.acceptInvitation({ token, mfaCode: code.trim(), ...(view.passwordRequired ? { newPassword: password } : {}) });
    setBusy(false);
    if (res.ok) {
      setCodes(res.value.recoveryCodes);
      return;
    }
    const reason = res.error.reasonCode ?? '';
    setMsg(reason === 'MFA_CODE_INVALID' ? t('inv.badCode') : reason === 'PASSWORD_POLICY' ? t('inv.weak') : t('inv.invalid'));
  };

  return (
    <AuthLayout>
      <AuthCard>
        <h1 className="vello-auth-heading__title">{view?.purpose === 'MFA_RESET' ? t('inv.titleReset') : t('inv.title')}</h1>
        {problem !== undefined && <Notice tone="danger">{problem}</Notice>}
        {problem === undefined && view === undefined && <p>{t('inv.loading')}</p>}
        {codes !== undefined && (
          <div data-testid="inv-codes">
            <strong>{t('inv.codes.title')}</strong>
            <Text as="p" tone="secondary">{t('inv.codes.body')}</Text>
            <CodeColumns>
              {codes.map((c) => <li key={c}>{c}</li>)}
            </CodeColumns>
            <Button variant="primary" block onClick={() => navigate('login')}>{t('inv.toLogin')}</Button>
          </div>
        )}
        {view !== undefined && codes === undefined && (
          <form onSubmit={(e) => void onSubmit(e)} noValidate data-testid="inv-form">
            <Stack gap={4}>
              <Text as="p" tone="secondary">{t('inv.account', { identifier: view.identifier })}</Text>
              <p>{t('inv.step.app')}</p>
              <Text as="code" mono block data-testid="inv-setup-key">{view.totp.setupKey}</Text>
              <TapLink href={view.totp.otpauthUri}>{t('inv.step.link')}</TapLink>
              <Field label={t('inv.field.code')} htmlFor="inv-code">
                <TextInput id="inv-code" inputMode="numeric" autoComplete="one-time-code" className="tnum" value={code} onChange={(e) => setCode(e.target.value)} required />
              </Field>
              {view.passwordRequired && (
                <>
                  <Field label={t('inv.field.password')} htmlFor="inv-password" hint={t('inv.password.hint')}>
                    <TextInput id="inv-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </Field>
                  <Field label={t('inv.field.confirm')} htmlFor="inv-confirm">
                    <TextInput id="inv-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                  </Field>
                </>
              )}
              {msg !== undefined && <Notice tone="danger">{msg}</Notice>}
              <Button type="submit" variant="primary" block disabled={busy} data-testid="inv-submit">{busy ? t('inv.busy') : t('inv.submit')}</Button>
            </Stack>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
